import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import Layout from "./components/Layout";
import LoginPage from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import DailyReview from "./pages/DailyReview";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Tasks from "./pages/Tasks";
import Approvals from "./pages/Approvals";
import Procurement from "./pages/Procurement";
import InstallCalendar from "./pages/InstallCalendar";
import Tickets from "./pages/Tickets";
import Finance from "./pages/Finance";
import Reports from "./pages/Reports";
import Team from "./pages/Team";
import HR from "./pages/HR";
import Settings from "./pages/Settings";
import AuditLogs from "./pages/AuditLogs";
import CleanupTools from "./pages/CleanupTools";
import IncentiveSlabs from "./pages/IncentiveSlabs";
import SiteVisits from "./pages/SiteVisits";
import Design from "./pages/Design";
import "./App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function AuthedApp() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/daily" element={<Protected><DailyReview /></Protected>} />
      <Route path="/projects" element={<Protected><Projects /></Protected>} />
      <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
      <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
      <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
      <Route path="/procurement" element={<Protected><Procurement /></Protected>} />
      <Route path="/calendar" element={<Protected><InstallCalendar /></Protected>} />
      <Route path="/site-visits" element={<Protected><SiteVisits /></Protected>} />
      <Route path="/design" element={<Protected><Design /></Protected>} />
      <Route path="/tickets" element={<Protected><Tickets /></Protected>} />
      <Route path="/finance" element={<Protected><Finance /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/team" element={<Protected><Team /></Protected>} />
      <Route path="/hr" element={<Protected><HR /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/settings/audit-logs" element={<AdminOnly><AuditLogs /></AdminOnly>} />
      <Route path="/settings/cleanup" element={<AdminOnly><CleanupTools /></AdminOnly>} />
      <Route path="/settings/incentive-slabs" element={<AdminOnly><IncentiveSlabs /></AdminOnly>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (user) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AuthedApp />
        <Toaster richColors position="top-right" theme="dark" />
      </BrowserRouter>
    </AuthProvider>
  );
}
